import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import {CalendarOptions, DayHeaderContentArg, EventClickArg, EventMountArg} from '@fullcalendar/angular';
import {statesCaptionEntities} from '@constants/states-caption.constants';
import {DiaryEvent, DiaryFilters} from '@shared/models/diary.model';
import {
  diaryEventTypeIcons,
  diaryViewFullCalendar,
  diaryViewIds,
  diaryViewKeys, diaryViewValues
} from '@constants/diary.constants';
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import allLocales from '@fullcalendar/core/locales-all';
import * as moment from 'moment';
import {
  GenericFunctionsUtil, IdName
} from 'front-base-angular-core/angular/projects/front-base-angular-core/src/public-api';
import {timeMS} from '@constants/time.constants';
import {I18nService} from '@shared/services/i18n.service';
import {FullCalendar} from 'primeng/fullcalendar';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {EventInput} from '@fullcalendar/core';

@Component({
  selector: 'si-di-diary',
  templateUrl: './di-diary.component.html',
  styleUrls: ['./di-diary.component.scss']
})
export class DiDiaryComponent implements AfterViewInit, OnInit {
  @Input() filters: DiaryFilters;
  @Input() initialView: string;
  @Input() lastInitDate: Date;
  @Input() showFilters: boolean = true;
  @Input() showCaption: boolean = true;
  @Input() firstTime: boolean = true;
  @Input() isTabletWidth: boolean = false;
  @Output() reloadPaginationChange = new EventEmitter<any>();
  @Output() reloadEventsChange = new EventEmitter<any>();
  @ViewChild('fullCalendar') fullCalendar: FullCalendar;
  selectorValues: IdName[] = diaryViewValues;
  statesCaptionEntities = statesCaptionEntities;
  diaryViewKeys = diaryViewKeys;
  calendarOptions: CalendarOptions;
  calendarTitle: string;
  changedView: boolean;
  timeComparison: string = '';
  dateComparison: string = '';
  dateHeaderComparison = '';
  monthWeekHeaderComparison = '';
  private _events: DiaryEvent[];
  private _deleteEvents: number;

  constructor(
    private i18nService: I18nService,
    private translateService: TranslateService,
    private router: Router,
    private route: ActivatedRoute
  ) { }

  get events() {
    return this._events;
  }

  @Input()
  set events(val) {
    this._events = val || [] as DiaryEvent[];
    this.addEvents(val);
  }

  get deleteEvents() {
    return this._deleteEvents;
  }

  @Input()
  set deleteEvents(val) {
    this._deleteEvents = val;
    if (val) {
      this.deleteEventsFunction();
    }
  }

  ngOnInit(): void {
    this.initCalendarValues();
  }

  ngAfterViewInit(): void {
    document.querySelector('.fc-theme-standard').classList.remove('fc-theme-standard');
    this.addEvents(this.events);
  }

  initCalendarValues(): void {
    this.calendarOptions = {
      timeZone: 'local',
      plugins: [listPlugin, dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialDate: new Date(this.filters.date),
      initialView: this.initialView,
      contentHeight: 'auto',
      views: {
        [diaryViewFullCalendar[diaryViewIds.LIST_WEEK]]: {titleFormat: {month: 'long', day: '2-digit'}},
        [diaryViewFullCalendar[diaryViewIds.TIME_GRID_DAY]]: {titleFormat: {weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit'}},
        [diaryViewFullCalendar[diaryViewIds.TIME_GRID_WEEK]]: {titleFormat: {month: 'long', day: '2-digit'}},
        [diaryViewFullCalendar[diaryViewIds.LIST_YEAR]]: {duration: {years: 10}}
      },
      headerToolbar: false,
      weekends: true,
      locales: allLocales,
      locale: this.i18nService.getLanguageShortHyphen(),
      dayHeaders: this.filters.selectView.id !== diaryViewKeys.LISTDAY,
      slotEventOverlap: this.filters.selectView.id !== diaryViewKeys.LISTDAY,
      slotLabelFormat: {
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: 'short'
      },
      stickyHeaderDates: false,
      eventMinHeight: 32,
      expandRows: true,
      eventDidMount: (info: EventMountArg) => {
        const dateStart = this.getStartDate(info.event._instance.range.start.getTime());
        const dateEnd = info.event._instance.range.end;
        const dateHours = new Date(dateStart).getHours().toString();
        const dateDay = new Date(dateStart).setHours(0, 0, 0, 0).toString();
        const stateCode = info.event._def.extendedProps?.status?.code;
        const eventIcon = diaryEventTypeIcons[info.event._def.extendedProps?.type?.code];
        const timeText = moment(dateStart).utc().format('LT');
        const timeTextParsed = moment(dateStart).minutes(0).utc().format('LT');
        const infoTimeText = `${moment(dateStart).utc().format('LT')} - ${moment(dateEnd).utc().format('LT')}`;
        const durationMinutes = moment(dateEnd).diff(moment(dateStart), 'minutes');

        switch (this.fullCalendar.calendar.view.type) {
          case diaryViewFullCalendar[diaryViewIds.TIME_GRID_DAY]:
            info.el.innerHTML = this.createEventDesktopHTML(stateCode, infoTimeText, info.event._def.title, eventIcon);
            break;
          case diaryViewFullCalendar[diaryViewIds.TIME_GRID_WEEK]:
            if (durationMinutes > 45) {
              info.el.innerHTML = this.createEventWeekDesktopHTML(stateCode, timeText, info.event._def.title, eventIcon);
            } else {
              info.el.innerHTML = this.createEventDesktopHTML(stateCode, timeText, info.event._def.title, eventIcon);
            }

            break;
          case diaryViewFullCalendar[diaryViewIds.DAY_GRID_MONTH]:
            info.el.innerHTML = this.createEventMonthDesktopHTML(stateCode, timeText, info.event._def.title, eventIcon);
            break;
          case diaryViewFullCalendar[diaryViewIds.LIST_WEEK]:
            if (this.dateComparison === dateDay && this.timeComparison === dateHours) {
              info.el.innerHTML = this.createEventMobileHTML('', stateCode, timeText, info.event._def.title, eventIcon);
            } else {
              info.el.innerHTML = this.createEventMobileHTML(timeTextParsed, stateCode, timeText, info.event._def.title, eventIcon);
            }
            break;
          case diaryViewFullCalendar[diaryViewIds.LIST_MONTH]:
            if (this.dateComparison === dateDay) {
              info.el.innerHTML = this.createEventMobileHTML('', stateCode, timeText, info.event._def.title, eventIcon);
            } else {
              const localeKey = this.i18nService.getLanguageShortHyphen();
              const options: Intl.DateTimeFormatOptions = {weekday: 'short', day: '2-digit'};
              const str = new Date(dateStart).toLocaleDateString(localeKey, options);
              info.el.innerHTML = this.createEventMobileHTML(str, stateCode, timeText, info.event._def.title, eventIcon);
            }
            break;
          case diaryViewFullCalendar[diaryViewIds.LIST_YEAR]:
            info.el.innerHTML = this.createEventYearHTML(stateCode, timeText, info.event._def.title, eventIcon);
            break;
          default:
        }

        this.timeComparison = dateHours;
        this.dateComparison = dateDay;
      },
      dayHeaderContent: (args: DayHeaderContentArg): {html: string} => {
        const localeKey = this.i18nService.getLanguageShortHyphen();
        let options: Intl.DateTimeFormatOptions;
        let str: string;
        let headerWeek = '';
        let htmlObject: {html: string};
        let styleClass = '';
        switch (this.fullCalendar.calendar.view.type) {
          case diaryViewFullCalendar[diaryViewIds.TIME_GRID_DAY]:
            htmlObject = {html: ''};
            break;
          case diaryViewFullCalendar[diaryViewIds.LIST_WEEK]:
            options = {weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit'};
            str = new Date(args.date).toLocaleDateString(localeKey, options);
            styleClass = !this.dateHeaderComparison ? 'mt-0' : '';
            htmlObject = {html: this.createEventHeaderHTML(str, styleClass, true)};
            break;
          case diaryViewFullCalendar[diaryViewIds.LIST_YEAR]:
            options = {weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit'};
            str = new Date(args.date).toLocaleDateString(localeKey, options);
            if (this.sameDates(args.date, new Date())) {
              str = `${this.translateService.instant('CORE_DATEPICKER_TODAY')} ${GenericFunctionsUtil.capitalizeFirstLetter(str)}`;
            } else if (this.sameDates(args.date, new Date(new Date().getTime() + timeMS.TIME_MS_DAY))) {
              str = `${this.translateService.instant('SI_TOMORROW')} ${GenericFunctionsUtil.capitalizeFirstLetter(str)}`;
            } else if (this.sameDates(args.date, new Date(new Date().getTime() - timeMS.TIME_MS_DAY))) {
              str = `${this.translateService.instant('SI_YESTERDAY')} ${GenericFunctionsUtil.capitalizeFirstLetter(str)}`;
            }

            styleClass = !this.dateHeaderComparison ? 'mt-0 ml-0' : 'ml-0';
            htmlObject = {html: this.createEventHeaderHTML(str, styleClass, true)};
            break;
          case diaryViewFullCalendar[diaryViewIds.LIST_MONTH]:
            headerWeek = this.getWeekFromMonthDate(args.date, localeKey);
            styleClass = !this.monthWeekHeaderComparison ? 'mt-0' : '';
            if (this.monthWeekHeaderComparison === headerWeek) {
              htmlObject = {html: ''};
            } else if (this.monthWeekHeaderComparison !== headerWeek) {
              htmlObject = {html: this.createEventMonthHeaderHTML(headerWeek, styleClass)};
            }
            break;
          case diaryViewFullCalendar[diaryViewIds.TIME_GRID_WEEK]:
            options = {weekday: 'short', month: '2-digit', day: '2-digit'};
            str = new Date(args.date).toLocaleDateString(localeKey, options);
            styleClass = 'm-0';
            htmlObject = {html: this.createEventHeaderHTML(str, styleClass)};
            break;
          case diaryViewFullCalendar[diaryViewIds.DAY_GRID_MONTH]:
            options = {weekday: 'long'};
            str = new Date(args.date).toLocaleDateString(localeKey, options);
            htmlObject = {html: this.createEventMonthHeaderDesktopHTML(str)};
            break;
          default:
        }

        this.dateHeaderComparison = 'notFirst';
        this.monthWeekHeaderComparison = headerWeek;
        return htmlObject;
      },
      eventClick: this.eventClickEvent.bind(this),
      datesSet: this.onInitSetEvents.bind(this)
    }
  }

  onInitSetEvents(): void {
    if (this.firstTime) {
      this.updateCalendarOptions();
      this.reloadEventsChange.emit();
    }
  }

  eventClickEvent(info: EventClickArg): void {
    this.router.navigate(['diary', info.event.id, 'detail']);
  }

  updateCalendarOptions(): void {
    this.fullCalendar.calendar.setOption('dayHeaders', true);
    this.fullCalendar.calendar.currentData.currentViewType === diaryViewFullCalendar[diaryViewIds.LIST_WEEK]
      ? this.fullCalendar.calendar.setOption('slotEventOverlap', true)
      : this.fullCalendar.calendar.setOption('slotEventOverlap', false);
  }

  getStartDate(startDateNumber: number): number {
    if (this.lastInitDate) {
      return Math.max(startDateNumber, this.lastInitDate?.getTime());
    } else {
      return startDateNumber;
    }
  }

  sameDates(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear()
      && date1.getMonth() === date2.getMonth()
      && date1.getDate() === date2.getDate();
  }

  createEventDesktopHTML(stateCode: string, time: string, title: string, icon: string): string {
    const eventHTML =
      `<div class="pasi-diary_event">
        <div class="d-flex align-items-center w-100">
          <div class="pasi-states-caption-states entity-${statesCaptionEntities.DIARY_EVENT} state_${stateCode}"></div>
          <div class="text-nowrap ml-2">${time}</div>
          <div class="pasi-diary_event_v-line mx-2"></div>
          <div class="pasi-diary_event_field mr-2">${title}</div>
          <div class="ml-auto">
            <i class="${icon}"></i>
          </div>
        </div>
      </div>`;
    return eventHTML;
  }

  getWeekFromMonthDate(currentDate: Date, localeKey?: string): string {
    const options: Intl.DateTimeFormatOptions = {month: 'long', day: 'numeric'};
    const initDate = moment(currentDate).startOf('isoWeek').toDate().toLocaleDateString(localeKey, options); // First week day
    const endDate = moment(currentDate).endOf('isoWeek').toDate().toLocaleDateString(localeKey, options); // Last week day
    return `${initDate} - ${endDate}`
  }

  createEventWeekDesktopHTML(stateCode: string, time: string, title: string, icon: string): string {
    const eventHTML =
      `<div class="pasi-diary_event d-block">
        <div class="d-flex align-items-center w-100">
          <div class="mr-1 pasi-states-caption-states entity-${statesCaptionEntities.DIARY_EVENT} state_${stateCode}"></div>
          <div class="pasi-diary_event_field">${time}</div>
          <div class="ml-auto">
            <i class="${icon}"></i>
          </div>
        </div>
        <div class="pasi-diary_event_field">${title}</div>
      </div>`;
    return eventHTML;
  }

  createEventMonthDesktopHTML(stateCode: string, time: string, title: string, icon: string): string {
    const eventHTML =
      `<div class="pasi-diary_event d-block">
        <div class="d-flex align-items-center w-100">
          <div class="pasi-states-caption-states entity-${statesCaptionEntities.DIARY_EVENT} state_${stateCode}"></div>
          <div class="text-nowrap ml-2">${time}</div>
          <div class="ml-auto">
            <i class="${icon}"></i>
          </div>
        </div>
        <div class="pasi-diary_event_field">${title}</div>
      </div>`;
    return eventHTML;
  }

  createEventYearHTML(stateCode: string, time: string, title: string, icon: string, hasSeparator?: boolean): string {
    const eventHTML =
      `<div class="pasi-diary_list w-100 d-flex mb-2">
        <div class="pasi-diary_event">
          <div class="pasi-diary_event_icon pasi-states-caption-states entity-${statesCaptionEntities.DIARY_EVENT} state_${stateCode}"></div>
          <div class="text-nowrap ml-2">${time}</div>
          <div class="pasi-diary_event_v-line mx-2"></div>
          <div class="pasi-diary_event_field text-wrap text-break">${title}</div>
          <div class="ml-auto">
            <i class="${icon}"></i>
          </div>
        </div>
      </div>`;
    const separator = `<div class="pasi-diary_h-line mb-2"></div>`;
    return hasSeparator ? separator + eventHTML : eventHTML;
  }

  createEventMobileHTML(timeTitle: string, stateCode: string, time: string, title: string, icon: string, hasSeparator?: boolean): string {
    const timeTitleCap = GenericFunctionsUtil.capitalizeFirstLetter(timeTitle);
    const eventHTML =
      `<div class="pasi-diary_list w-100 d-flex mb-2">
        <div class="pasi-diary_time">${timeTitleCap}</div>
        <div class="pasi-diary_event">
          <div class="pasi-diary_event_icon pasi-states-caption-states entity-${statesCaptionEntities.DIARY_EVENT} state_${stateCode}"></div>
          <div class="text-nowrap ml-2">${time}</div>
          <div class="pasi-diary_event_v-line mx-2"></div>
          <div class="pasi-diary_event_field text-wrap text-break">${title}</div>
          <div class="ml-auto">
            <i class="${icon}"></i>
          </div>
        </div>
      </div>`;
    const separator = `<div class="pasi-diary_h-line mb-2"></div>`;
    return hasSeparator ? separator + eventHTML : eventHTML;
  }

  createEventHeaderHTML(dateStr: string, styleClass: string, hasSeparator?: boolean): string {
    const dateStrCap = GenericFunctionsUtil.capitalizeFirstLetter(dateStr);
    const eventHTML =
      `<div class="pasi-diary_title ${styleClass}">
        <span>${dateStrCap}</span>
      </div>`;
    const separator = `<div class="pasi-diary_h-line mb-2"></div>`;
    return hasSeparator ? eventHTML + separator : eventHTML;
  }

  createEventMonthHeaderHTML(dateStr: string, styleClass?: string): string {
    const dateStrCap = GenericFunctionsUtil.capitalizeFirstLetter(dateStr);
    const eventHTML =
      `<div class="pasi-diary_title ${styleClass}">
        <span>${dateStrCap}</span>
      </div>
      <div class="pasi-diary_h-line mb-2"></div>`;
    return eventHTML;
  }

  createEventMonthHeaderDesktopHTML(dateStr?: string, styleClass?: string): string {
    const dateStrCap = GenericFunctionsUtil.capitalizeFirstLetter(dateStr);
    const eventHTML =
      `<div class="pasi-diary_pretitle ${styleClass}">
      <span>${dateStrCap}</span>
    </div>`;
    return eventHTML;
  }

  onChangeDate(date: number): void {
    if (!this.firstTime && !this.changedView) {
      this.deleteEventsFunction();
      this.checkPagedOptions();
      this.fullCalendar.calendar.gotoDate(date);
      this.reloadEventsChange.emit();
    }

    this.resetCalendarTitleAndVariables();
    this.updateQueryParams(date, this.filters.selectView?.code);
  }

  checkPagedOptions(): void {
    if (this.filters.selectView.id === diaryViewKeys.LISTYEAR) {
      this.reloadPaginationChange.emit();
      this.deleteEventsFunction();
    }
  }

  resetCalendarTitleAndVariables(): void {
    this.timeComparison = '';
    this.dateComparison = '';
    this.dateHeaderComparison = '';
    this.monthWeekHeaderComparison = '';
    this.calendarTitle = GenericFunctionsUtil.capitalizeFirstLetter(this.fullCalendar.calendar.currentData.viewTitle);
  }

  onSelectView(): void {
    switch (this.filters.selectView.id) {
      case diaryViewKeys.LISTDAY:
        this.filters.date = moment().valueOf();
        this.fullCalendar.calendar.gotoDate(this.filters.date);
        this.fullCalendar.calendar.changeView(diaryViewFullCalendar[diaryViewIds.TIME_GRID_DAY]);
        this.changedView = true;
        this.onChangeViewSetEvents();
        break;
      case diaryViewKeys.LISTWEEK:
        this.filters.date = moment().startOf('isoWeek').valueOf()
        this.fullCalendar.calendar.gotoDate(this.filters.date);
        if (this.isTabletWidth) {
          this.fullCalendar.calendar.changeView(diaryViewFullCalendar[diaryViewIds.TIME_GRID_WEEK]);
        } else {
          this.fullCalendar.calendar.changeView(diaryViewFullCalendar[diaryViewIds.LIST_WEEK]);
        }

        this.changedView = true;
        this.onChangeViewSetEvents();
        break;
      case diaryViewKeys.LISTMONTH:
        this.filters.date = moment().startOf('month').valueOf();
        this.fullCalendar.calendar.gotoDate(this.filters.date);
        if (this.isTabletWidth) {
          this.fullCalendar.calendar.changeView(diaryViewFullCalendar[diaryViewIds.DAY_GRID_MONTH]);
        } else {
          this.fullCalendar.calendar.changeView(diaryViewFullCalendar[diaryViewIds.LIST_MONTH]);
        }

        this.changedView = true;
        this.onChangeViewSetEvents();
        break;
      case diaryViewKeys.LISTYEAR:
        this.filters.date = moment().valueOf();
        this.fullCalendar.calendar.gotoDate(this.filters.date);
        this.fullCalendar.calendar.changeView(diaryViewFullCalendar[diaryViewIds.LIST_YEAR]);
        this.changedView = true;
        this.onChangeViewSetEvents();
        break;
      default:
    }

    this.updateQueryParams(this.filters.date, this.filters.selectView?.code);
  }

  async onChangeViewSetEvents(): Promise<void> {
    if (this.changedView) {
      this.deleteEventsFunction();
      this.resetCalendarTitleAndVariables();
      this.updateCalendarOptions();
      this.checkPagedOptions();
      this.reloadEventsChange.emit();
      this.changedView = false;
    }
  }

  onClickNext(): void {
    this.fullCalendar.calendar.next();
    this.filters.date = this.fullCalendar.calendar.currentData.currentDate;
    this.deleteEventsFunction();
  }

  onClickPrev(): void {
    this.fullCalendar.calendar.prev();
    this.filters.date = this.fullCalendar.calendar.currentData.currentDate;
    this.deleteEventsFunction();
  }

  updateQueryParams(date: number, view: string): void {
    this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: {date, view},
        queryParamsHandling: 'merge',
        replaceUrl: true
      }
    );
  }

  deleteEventsFunction(): void {
    const removeEvents = this.fullCalendar.calendar.getEvents();
    removeEvents.forEach(event => event.remove());
  }

  addEvents(events: DiaryEvent[]): void {
    if (this.firstTime || !this.fullCalendar) {
      return;
    }

    const transformedEvents = events.map(event => this.parseEventToFullCalendar(event));
    this.fullCalendar.calendar.addEventSource(transformedEvents);
  }

  parseEventToFullCalendar(event: DiaryEvent): EventInput {
    return {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      extendedProps: {
        ...event
      }
    } as EventInput;
  }
}
